(() => {
  'use strict';

  const WISHLIST_KEY = 'rd-varada-wishlist-v1';
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  const getWishlist = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
      if (Array.isArray(parsed)) return parsed.map(String);
      if (Array.isArray(parsed?.items)) return parsed.items.map(String);
    } catch (error) {
      // A malformed or unavailable localStorage value should never block the header.
    }
    return [];
  };

  const saveWishlist = (items) => {
    const uniqueItems = [...new Set(items.map(String).filter(Boolean))];
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(uniqueItems));
    } catch (error) {
      // Private browsing/storage restrictions: keep the UI usable without persistence.
    }
    document.dispatchEvent(new CustomEvent('rd:wishlist:changed', { detail: { items: uniqueItems } }));
    return uniqueItems;
  };

  const updateWishlistUI = () => {
    const items = getWishlist();
    const count = items.length;

    document.querySelectorAll('[data-rd-wishlist-count]').forEach((element) => {
      element.textContent = count > 99 ? '99+' : String(count);
      element.hidden = count === 0;
    });

    document.querySelectorAll('[data-rd-wishlist-toggle]').forEach((button) => {
      const key = String(button.dataset.productHandle || button.dataset.productId || '');
      const active = Boolean(key && items.includes(key));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  };

  const initialiseWishlist = () => {
    if (window.__rdVaradaWishlistInitialised) {
      updateWishlistUI();
      return;
    }

    window.__rdVaradaWishlistInitialised = true;

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rd-wishlist-toggle]');
      if (!button) return;

      const key = String(button.dataset.productHandle || button.dataset.productId || '');
      if (!key) return;

      event.preventDefault();
      const items = getWishlist();
      const nextItems = items.includes(key) ? items.filter((item) => item !== key) : [...items, key];
      saveWishlist(nextItems);
    });

    document.addEventListener('rd:wishlist:changed', updateWishlistUI);
    window.addEventListener('storage', (event) => {
      if (event.key === WISHLIST_KEY) updateWishlistUI();
    });

    updateWishlistUI();
  };

  const closeDetails = (details) => {
    if (details?.open) details.removeAttribute('open');
  };

  const closeOtherDesktopMenus = (current) => {
    document.querySelectorAll('[data-rd-nav-details][open], [data-rd-category-menu][open]').forEach((details) => {
      if (details !== current) closeDetails(details);
    });
  };

  const initialisePredictiveSearchEmptyState = (root) => {
    if (!window.customElements?.whenDefined) return;

    window.customElements.whenDefined('predictive-search').then(() => {
      root.querySelectorAll('predictive-search.rd-vh-search').forEach((searchElement) => {
        if (searchElement.dataset.rdEmptyStateInitialised === 'true') return;
        searchElement.dataset.rdEmptyStateInitialised = 'true';

        const input = searchElement.querySelector('input[type="search"]');
        const results = searchElement.querySelector('[data-predictive-search]');
        const template = searchElement.querySelector('[data-rd-search-empty-template]');
        const reset = searchElement.querySelector('button[type="reset"]');
        if (!input || !results || !template) return;

        const restore = (openPanel = false) => {
          if (input.value.trim()) return;
          results.innerHTML = template.innerHTML;
          searchElement.removeAttribute('results');

          if (openPanel && typeof searchElement.open === 'function') {
            searchElement.open();
          }
        };

        input.addEventListener('focus', () => {
          if (!input.value.trim()) requestAnimationFrame(() => restore(true));
        });

        input.addEventListener('input', () => {
          if (!input.value.trim()) {
            window.setTimeout(() => restore(document.activeElement === input), 340);
          }
        });

        reset?.addEventListener('click', () => {
          window.setTimeout(() => restore(true), 340);
        });
      });
    });
  };

  const initialiseMobileDrawer = (root) => {
    root.querySelectorAll('[data-rd-mobile-drawer]').forEach((drawer) => {
      if (drawer.dataset.rdInitialised === 'true') return;
      drawer.dataset.rdInitialised = 'true';

      const close = () => {
        drawer.removeAttribute('open');
        document.body.classList.remove('rd-vh-menu-open');
        drawer.querySelector('summary')?.focus({ preventScroll: true });
      };

      drawer.addEventListener('toggle', () => {
        document.body.classList.toggle('rd-vh-menu-open', drawer.open);
      });

      drawer.querySelectorAll('[data-rd-menu-close]').forEach((button) => {
        button.addEventListener('click', close);
      });

      drawer.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          document.body.classList.remove('rd-vh-menu-open');
        });
      });
    });
  };

  const initialiseDesktopNavigation = (root) => {
    const detailsElements = root.querySelectorAll('[data-rd-nav-details], [data-rd-category-menu]');

    detailsElements.forEach((details) => {
      if (details.dataset.rdInitialised === 'true') return;
      details.dataset.rdInitialised = 'true';

      details.addEventListener('toggle', () => {
        if (details.open) closeOtherDesktopMenus(details);
      });

      if (details.matches('[data-rd-nav-details]')) {
        let closeTimer;
        const parent = details.closest('.rd-vh-nav__item');

        parent?.addEventListener('pointerenter', (event) => {
          if (!desktopQuery.matches || event.pointerType === 'touch') return;
          window.clearTimeout(closeTimer);
          closeOtherDesktopMenus(details);
          details.setAttribute('open', '');
        });

        parent?.addEventListener('pointerleave', (event) => {
          if (!desktopQuery.matches || event.pointerType === 'touch') return;
          closeTimer = window.setTimeout(() => {
            if (!details.contains(document.activeElement)) closeDetails(details);
          }, 130);
        });
      }
    });
  };

  const initialiseHeader = (root) => {
    if (!root || root.dataset.rdInitialised === 'true') return;
    root.dataset.rdInitialised = 'true';

    const section = root.closest('.section-header') || root.parentElement;
    const computed = window.getComputedStyle(root);

    // Expose brand variables to the native cart drawer, which sits outside the header DOM tree.
    ['--rd-vh-accent', '--rd-vh-accent-hover', '--rd-vh-border', '--rd-vh-text'].forEach((property) => {
      const value = computed.getPropertyValue(property).trim();
      if (value) document.documentElement.style.setProperty(property, value);
    });

    const updateHeight = () => {
      const height = Math.ceil(section?.getBoundingClientRect().height || root.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--rd-vh-sticky-height', `${height}px`);
    };

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(root);
    }
    updateHeight();

    let ticking = false;
    const updateScrollState = () => {
      const scrolled = window.scrollY > 8;
      root.classList.toggle('is-scrolled', scrolled);
      section?.classList.toggle('rd-vh-is-scrolled', scrolled);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateScrollState);
      },
      { passive: true },
    );
    updateScrollState();

    initialiseMobileDrawer(root);
    initialiseDesktopNavigation(root);
    initialisePredictiveSearchEmptyState(root);
    initialiseWishlist();
  };

  const initialiseAll = (scope = document) => {
    scope.querySelectorAll?.('[data-rd-varada-header]').forEach(initialiseHeader);
  };

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-rd-nav-details][open], [data-rd-category-menu][open]').forEach((details) => {
      if (!details.contains(event.target)) closeDetails(details);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('[data-rd-nav-details][open], [data-rd-category-menu][open]').forEach((details) => {
      closeDetails(details);
      details.querySelector('summary')?.focus({ preventScroll: true });
    });

    document.querySelectorAll('[data-rd-mobile-drawer][open]').forEach((drawer) => {
      drawer.removeAttribute('open');
      document.body.classList.remove('rd-vh-menu-open');
      drawer.querySelector('summary')?.focus({ preventScroll: true });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();